import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { DataService } from 'src/core/services/data.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { Table, TableModule } from 'primeng/table';
import { ChartConfiguration, ChartDataset, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ChatService } from 'src/core/services/chat.service';
import { DaterangeService } from 'src/core/services/daterange.service';
import { combineLatest, defer, distinctUntilChanged, EMPTY, filter, iif, map, merge, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ModalService } from 'src/core/services/modal.service';
import { LoadingService } from 'src/core/services/loading.service';
import { WidgetService } from 'src/core/services/widget.service';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { NgShuiModule } from '@shui/angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import 'chartjs-adapter-date-fns';
import { FilterService, SortEvent } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { EventDetails, EventLogItem, SubsystemErrors } from 'src/core/types/systemhealth.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from 'src/core/types/general.types';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { environment } from 'src/environments/environment';
import { DeviceService } from 'src/core/services/device.service';
import { AppConfigService } from 'src/core/services/appconfig.service';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

@Component({
  selector: 'app-errors',
  imports: [CommonModule, TableModule, BaseChartDirective, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './errors.component.html',
  styleUrl: './errors.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ErrorsComponent {
  readonly scatterFilterOptions = [
    { key: 'warnings', label: 'errors.events.filterOption.warnings', severity: 'Warning', anomaly: false },
    { key: 'errors', label: 'errors.events.filterOption.errors', severity: 'Error', anomaly: false },
    { key: 'anomalyWarning', label: 'errors.events.filterOption.anomalyWarning', severity: 'Warning', anomaly: true },
    { key: 'anomalyError', label: 'errors.events.filterOption.anomalyError', severity: 'Error', anomaly: true }
  ] as const;

  private readonly severityTranslationKeys: Record<string, string> = {
    warning: 'errors.events.filterOption.warnings',
    warnings: 'errors.events.filterOption.warnings',
    error: 'errors.events.filterOption.errors',
    errors: 'errors.events.filterOption.errors',
    'anomaly warning': 'errors.events.filterOption.anomalyWarning',
    'anomaly error': 'errors.events.filterOption.anomalyError'
  };

  anomaliesEnabled: boolean = false;

  private readonly anomalyWarningPointColor = '#3DC3B0';
  private readonly anomalyErrorPointColor = '#2A9A8A';
  private readonly errorPointColor = '#B21B5C';
  private readonly warningPointColor = '#F19E6A';
  private readonly dimmedPointColor = 'rgba(0, 0, 0, 0.1)';
  private readonly anomalyTrendColor = '#3DC3B0';

  constructor(
    private dataService: DataService,
    private chatService: ChatService,
    private selectedSystemService: SelectedsystemService,
    private daterangeService: DaterangeService,
    private modalService: ModalService,
    protected loadingService: LoadingService, private widgetService: WidgetService,
    private filterService: FilterService,
  private selectedContentService: SelectedContentService,
  private deviceService: DeviceService,
  private appConfigService: AppConfigService,
  private translocoService: TranslocoService) { }

  @ViewChild('table') table: Table;
  @ViewChild('tabletwo') tabletwo: Table;
  @ViewChild('tooltip') tooltip: any;

  errors: any = null;
  private allErrors: any[] = [];
  subsystemdata;
  isLoading: boolean = true;
  isLoadingSubby: boolean = true;
  isError: boolean = false;
  isErrorSubby: boolean = false;
  searchTerm: string = '';;
  searchTermDetails: string = '';;
  selectedRange: number;
  selectedSubsystem: string = null;

  @Input() tabs: any[] = [];
  selectedWidget: string = "None";
  displayedTabs: any[] = [];
  activeTab: string = "Activities";
  workareaSize: workareaSize = "small";
  screenSize: string = 'Large';
   onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  }
  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
  switchTab = this.widgetService.switchTab.bind(this.widgetService);
  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory') => {
    this.daterangeService.changeRange(range, type);
  };

  isDateFiltered: boolean = false;
  private destroyRef = inject(DestroyRef);
  selectedScatterFilters = this.scatterFilterOptions.map(option => option.key);
  private scatterEvents: Array<EventLogItem & { searchable?: string; sequence?: number }> = [];

  now = new Date();
  sevenDaysAgo = new Date(this.now.getTime() - 7 * 24 * 60 * 60 * 1000);
  businessPartnerMode = environment.businessPartnerMode;

  @ViewChild('scatter', { read: BaseChartDirective }) chartRef!: BaseChartDirective;
  public chartConfig: {} = {};
  private suppressZoom = false;
  emptyChartData = {
    datasets: [] // no data
  };
  emptyChartOptions: ChartOptions<'scatter'> = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 4,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      },
      annotation: {
        annotations: {
          centerLabel: {
            type: 'label',
            xValue: (ctx) => {
              const xScale = ctx.chart.scales["x"];
              return (xScale.max + xScale.min) / 2;
            },
            yValue: (ctx) => {
              const xScale = ctx.chart.scales["y"];
              return (xScale.max + xScale.min) / 2;
            },
            content: [this.translocoService.translate('errors.chart.annotation')],
            color: '#000000',
            font: {
              size: 14,
              family: 'Siemens Sans',
              weight: 'bold'
            },
            textAlign: 'center',
            backgroundColor: '#FDD57026',
            padding: 24,
            borderRadius: 6,
          }
        }
      },
    },
    scales: {
      x: {
        type: 'time',
        min: this.sevenDaysAgo.toISOString(),
        max: this.now.toISOString(),
        ticks: {
          maxTicksLimit: 5,
          color: '#7f8489'
        },
        time: {
          tooltipFormat: 'yyyy-MM-dd HH:mm',
          unit: 'minute',
          displayFormats: {
            minute: 'yyyy-MM-dd HH:mm'
          }
        },
        title: {
          display: false,
          text: 'Error Events'
        }
      },
      y: {
        type: 'linear',
        ticks: {
          color: '#7f8489'
        },
        title: {
          display: false,
          text: 'Y Axis'
        }
      }
    }
  };
  public chartConfigs: { [key: string]: ChartConfiguration<'line'> } = {};
  public anomalyChartConfigs: { [key: string]: ChartConfiguration<'line'> } = {};

  private baseDatasetStyle: Partial<ChartDataset<'line'>> = {
    backgroundColor: '#FBBF28',
    borderColor: '#FBBF28',
    pointBackgroundColor: '#FBBF28',
    pointBorderColor: '#FBBF28',
    pointBorderWidth: 0,
    pointRadius: 2.5,
    pointHoverRadius: 2.5,
    borderWidth: 4,
    tension: 0.35,
    fill: 'origin'
  };




  ngOnInit(): void {

    this.filterService.register('betweenDates', (value: Date | string, filter: { min: Date, max: Date }): boolean => {
      if (!value || !filter?.min || !filter?.max) return false;
      const date = new Date(value);
      return date >= filter.min && date <= filter.max;
    });

    this.loadingService.$isErrorsLoading.subscribe((loads) => {
      this.isLoading = loads;
    });

    this.loadingService.$isErrorsError.subscribe((error) => {
      this.isError = error;
    });

    this.loadingService.$isSubbyLoading.subscribe((loads) => {
      this.isLoadingSubby = loads;
    });

    this.loadingService.$isSubbyError.subscribe((error) => {
      this.isErrorSubby = error;
    });

    this.selectedSystemService.$selectedSubsystem.subscribe((sys) => {
      this.selectedSubsystem = sys;
    });

    this.daterangeService.$selectedRangeSystemHealth.subscribe((range) => {
      this.selectedRange = range;
    });

    //Check which tabs to display
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);
      }
    });

    //Check which tab is currently active
    this.widgetService.$activeTab.subscribe((tab) => {
      if (tab) {
        this.activeTab = tab;
      }
      else {
        this.activeTab = this.displayedTabs[0]?.name;
      }
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    this.deviceService.size$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((size) => {
      this.screenSize = size;
    });

    this.appConfigService.featureFlags$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((flags) => {
      const anomaliesChanged = this.anomaliesEnabled !== flags.anomalies;
      this.anomaliesEnabled = flags.anomalies;
      if (anomaliesChanged) {
        this.selectedScatterFilters = this.activeFilterOptions.map(option => option.key);
        if (this.scatterEvents.length > 0) {
          this.updateScatterChart();
        }
        this.applyEventTypeFilter();
      }
    });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeSystemHealth;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshSubsystems.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isSubbyLoading.next(true);
        this.loadingService.$isSubbyError.next(false);
        this.subsystemdata = null;
        this.chartConfigs = {};
        this.anomalyChartConfigs = {};
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.subsystemPullDate;
        const pulledFor = this.selectedSystemService.subsystemPulledFor;

        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';

        const shouldFetch =
          force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== (serial + material);

        return iif(
          () => shouldFetch,
          this.dataService.getErrorSummary(material, serial).pipe(
            tap(({ response, status }: ApiResponse<SubsystemErrors>) => {
              console.log(force ? 'Forced refresh of subsystem data' : 'Fetching new subsystem data');
              this.selectedSystemService.subsystemPullDate = now;
              this.selectedSystemService.subsystemPulledFor = serial + material;
              if (status === 200) {
                this.selectedSystemService.$subsystemdata.next(response);
              } else if (status === 204) {
                this.selectedSystemService.$subsystemdata.next(null);
                this.loadingService.$isSubbyEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$subsystemdata.pipe(
            take(1),
            tap(() => console.log('Using cached subsystem data')),
            map((cached): ApiResponse<SubsystemErrors> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response, status }: ApiResponse<SubsystemErrors>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ response, rangeValue }) => {
        
        if (!response.length) {
          response = [];
          this.loadingService.$isErrorsError.next(false);
        }
        this.subsystemdata = response;
        response?.forEach(row => {
          const key = row.subsystem;
          const trend = row.metrics.time_windows[String(rangeValue)]?.Trend ?? [];
          this.chartConfigs[key] = this.createChartSubsystem(trend);
          this.anomalyChartConfigs[key] = this.createChartSubsystem(this.getSubsystemAnomalyTrend(row), this.anomalyTrendColor);
        });

        this.loadingService.$isSubbyLoading.next(false);
        this.loadingService.$isSubbyError.next(false);
        this.loadingService.$isErrorsLoading.next(false);
      },
      error: err => {
        console.error('Error fetching subsystem summary', err);
        this.subsystemdata = [];
        this.loadingService.$isErrorsError.next(true);
        this.loadingService.$isSubbyLoading.next(false);
        this.loadingService.$isSubbyError.next(false);
      }
    });

    //Listening to changed Daterange
    const subby$ = this.selectedSystemService.$selectedSubsystem;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggersEvents$ = merge(
      subby$.pipe(
        filter((subsystem): subsystem is string => subsystem !== null),
        map(subsystem => ({ subsystem, force: false }))),
      this.selectedSystemService.$refreshEvents.pipe(
        withLatestFrom(subby$),
        map(([, subsystem]) => ({ subsystem, force: true }))
      )
    );

    triggersEvents$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isErrorsLoading.next(true);
        this.loadingService.$isErrorsError.next(false);
        this.errors = null;
        this.scatterEvents = [];
        this.chartConfig = this.createChart([]);
      }),
      switchMap(({ subsystem, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.errorsPullDate;
        const pulledFor = this.selectedSystemService.errorsPulledFor;

        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';

        const shouldFetch =
          force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== (serial + material + subsystem);

        return iif(
          () => shouldFetch,
          this.dataService.getErrors(material, serial, subsystem).pipe(
            tap(({ response, status }: ApiResponse<EventDetails>) => {
              console.log(force ? 'Forced refresh of events data' : 'Fetching new events data');
              this.selectedSystemService.errorsPullDate = now;
              this.selectedSystemService.errorsPulledFor = serial + material + subsystem;
              if (status === 200) {
                this.selectedSystemService.$errors.next(response);
              } else if (status === 204) {
                this.selectedSystemService.$errors.next(null);
                this.loadingService.$isErrorsEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$errors.pipe(
            take(1),
            tap(() => console.log('Using cached event data')),
            map((cached): ApiResponse<EventDetails> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response, status }: ApiResponse<EventDetails>) => ({ response, subsystem }))
        )
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ response }: { response: EventDetails }) => {

        //Adding searchable field to data
        const searchableData = response?.events?.map((event, index) => ({
          ...event,
          searchable: `${event.EventCode} ${event.MessageText} ${event["Event Data"]}`
        }));
        if(searchableData) {
        let errorsAndSequenz = this.addSequenceNumbers(searchableData);
        this.allErrors = errorsAndSequenz;
        this.scatterEvents = errorsAndSequenz;
        this.allErrors.filter(e => e.BucketedTimestamp = new Date(e.BucketedTimestamp));
        this.applyEventTypeFilter();
        this.updateScatterChart();
        this.loadingService.$isErrorsLoading.next(false);
        } else {
          this.allErrors = [];
          this.errors = [];
          this.scatterEvents = [];
          this.chartConfig = this.createChart([]);
          this.loadingService.$isErrorsLoading.next(false);
          this.loadingService.$isErrorsError.next(true);
          this.isError = false;
      }
      },
      error: err => {
        console.error('Error fetching subsystem summary', err);
        this.allErrors = [];
        this.errors = [];
        this.loadingService.$isErrorsLoading.next(false);
        this.loadingService.$isErrorsError.next(true);
      }
    });

  }

  goToATA(url) {
    if(url) {
      window.open("https://"+ url, '_blank');
    } else {
      console.error('ATA information is not available.');
    }
  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  highlightDetails(text: string): string {
    if (!this.searchTermDetails || !text) return text;

    const escaped = this.searchTermDetails.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  askChat(code: string, severity: string) {
    this.widgetService.$chatPipSize.next('maximized');
    const prompt = this.translocoService.translate('errors.prompt.fixError', {
      severity: this.translateSeverity(severity),
      code,
      serialNumber: this.selectedContentService.currentSystem?.SerialNumber || ''
    });

    if (shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value)) {
      this.chatService.addMessage(prompt, true, undefined, true);
      return;
    }

    this.chatService.addMessage(prompt, true);

  }

  openCharts() {
    this.modalService.$modalChanged.next({ modal: "error", open: true })
  }

  private translateSeverity(severity: string): string {
    const normalizedSeverity = severity?.trim().toLowerCase();
    const translationKey = normalizedSeverity ? this.severityTranslationKeys[normalizedSeverity] : undefined;

    return translationKey ? this.translocoService.translate(translationKey) : severity;
  }

  selectSubsystemPowerBI(subsystem) {
    this.modalService.$modalChanged.next({ modal: "error", open: true, filter: [{value: subsystem , type: "text" }] })
  }

  private getScatterPointColor(event: { Severity?: string; anomaly_details?: { anomaly_2sd_flag?: boolean | null } }, dimmed = false): string {
    if (dimmed) {
      return this.dimmedPointColor;
    }

    if (this.anomaliesEnabled && event?.anomaly_details?.anomaly_2sd_flag === true) {
      return event?.Severity === 'Warning' ? this.anomalyWarningPointColor : this.anomalyErrorPointColor;
    }

    return event?.Severity === 'Warning' ? this.warningPointColor : this.errorPointColor;
  }

  isScatterFilterSelected(filterKey: typeof this.scatterFilterOptions[number]['key']): boolean {
    return this.selectedScatterFilters.includes(filterKey);
  }

  toggleScatterFilter(filterKey: typeof this.scatterFilterOptions[number]['key']) {
    if (this.isScatterFilterSelected(filterKey)) {
      this.selectedScatterFilters = this.selectedScatterFilters.filter(key => key !== filterKey);
    } else {
      this.selectedScatterFilters = [...this.selectedScatterFilters, filterKey];
    }

    this.updateScatterChart();
    this.applyEventTypeFilter();
  }

  get activeFilterOptions() {
    if (this.anomaliesEnabled) return this.scatterFilterOptions;
    return this.scatterFilterOptions.filter(option => !option.anomaly);
  }

  private applyEventTypeFilter() {
    const activeOptions = this.activeFilterOptions.filter(o => this.isScatterFilterSelected(o.key));
    if (activeOptions.length === this.activeFilterOptions.length) {
      this.errors = this.allErrors;
    } else {
      this.errors = this.allErrors.filter(event => {
        const eventSeverity = event?.Severity === 'Warning' ? 'Warning' : 'Error';
        const isAnomaly = this.anomaliesEnabled && event?.anomaly_details?.anomaly_2sd_flag === true;
        return activeOptions.some(o => o.severity === eventSeverity && o.anomaly === isAnomaly);
      });
    }
  }

  private updateScatterChart() {
    this.chartConfig = this.createChart(this.buildScatterDatasets(this.scatterEvents));
    this.syncScatterChartHighlight();
  }

  private buildScatterDatasets(events: Array<EventLogItem & { searchable?: string; sequence?: number }>) {
    return this.activeFilterOptions
      .filter(option => this.isScatterFilterSelected(option.key))
      .map(option => {
        const datasetEvents = events.filter(event => this.matchesScatterFilter(event, option.severity, option.anomaly));
        const points = datasetEvents.map(event => this.createScatterPoint(event));
        const pointColor = option.anomaly
          ? (option.severity === 'Warning' ? this.anomalyWarningPointColor : this.anomalyErrorPointColor)
          : (option.severity === 'Warning' ? this.warningPointColor : this.errorPointColor);

        return {
          label: option.label,
          data: points,
          backgroundColor: points.map(() => pointColor),
          borderColor: points.map(() => pointColor),
          pointRadius: 6
        };
      })
      .filter(dataset => dataset.data.length > 0);
  }

  private createScatterPoint(event: EventLogItem & { sequence?: number }) {
    return {
      x: new Date(event.BucketedTimestamp),
      y: event.sequence,
      severity: event.Severity,
      anomaly_2sd_flag: event.anomaly_details?.anomaly_2sd_flag ?? null
    };
  }

  private matchesScatterFilter(event: { Severity?: string; anomaly_details?: { anomaly_2sd_flag?: boolean | null } }, severity: 'Error' | 'Warning', anomaly: boolean): boolean {
    const eventSeverity = event?.Severity === 'Warning' ? 'Warning' : 'Error';
    const isAnomaly = this.anomaliesEnabled && event?.anomaly_details?.anomaly_2sd_flag === true;

    return eventSeverity === severity && isAnomaly === anomaly;
  }

  private syncScatterChartHighlight() {
    if (!this.chartRef?.chart) {
      return;
    }

    this.onTableFiltered({ filteredValue: this.tabletwo?.filteredValue ?? this.errors ?? [] });
  }

  getSubsystemAnomalyCount(subsystem: { metrics?: { time_windows?: Record<string, { ErrorAnomalies?: number | null; WarningAnomalies?: number | null }> } }): number {
    const metrics = subsystem?.metrics?.time_windows?.[`${this.selectedRange}`];

    return (metrics?.ErrorAnomalies ?? 0) + (metrics?.WarningAnomalies ?? 0);
  }

  getSubsystemAnomalyTrend(subsystem: { metrics?: { time_windows?: Record<string, { ErrorAnomalyTrend?: number[]; WarningAnomalyTrend?: number[] }> } }): number[] {
    const metrics = subsystem?.metrics?.time_windows?.[`${this.selectedRange}`];
    const errorTrend = metrics?.ErrorAnomalyTrend ?? [];
    const warningTrend = metrics?.WarningAnomalyTrend ?? [];
    const maxLen = Math.max(errorTrend.length, warningTrend.length);
    return Array.from({ length: maxLen }, (_, i) => (errorTrend[i] ?? 0) + (warningTrend[i] ?? 0));
  }

  hasNonZeroTrend(rowData?: number[] | null): boolean {
    return Array.isArray(rowData) && rowData.some(value => Number(value) !== 0);
  }

  createChart(rowData: any[]): ChartConfiguration<'scatter'> {
    return {
      type: 'scatter',
      data: {
        datasets: rowData
      },
      options: {
        maintainAspectRatio: false,
        devicePixelRatio: 4,
        scales: {
          x: {
            type: 'time',
            ticks: {
              maxTicksLimit: 5,
              color: '#7f8489'
            },
            time: {
              tooltipFormat: 'yyyy-MM-dd HH:mm',
              unit: 'minute',
              displayFormats: {
                minute: 'yyyy-MM-dd HH:mm'
              }
            },
            title: {
              display: false,
              text: 'Error Events'
            }
          },
          y: {
            title: {
              display: false,
              text: 'Sequenz'
            },
            ticks: {
              precision: 0,
              color: '#7f8489'
            }
          }
        },
        plugins: {
          annotation: {
            annotations: {}  // <-- we'll set this dynamically
          },
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const rawPoint = context.raw as { anomaly_2sd_flag?: boolean | null; severity?: string | null } | undefined;
                const severity = rawPoint?.severity === 'Warning' || `${context.dataset.label || ''}`.includes('Warning')
                  ? 'Warning'
                  : 'Error';
                const isAnomaly = this.anomaliesEnabled && rawPoint?.anomaly_2sd_flag === true;
                const labelPrefix = isAnomaly ? `Anomaly (${severity})` : severity;

                return `${labelPrefix}: ${context.formattedValue}`;
              }
            }
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: false,     // ✅ enable wheel zoom
                modifierKey: null  // you can also require 'ctrl', 'shift', etc.
              },
              drag: {
                enabled: true,
                borderColor: 'gray',
                borderWidth: 1,
                backgroundColor: 'rgba(200,200,200,0.3)'
              },
              mode: 'x',
              onZoomComplete: ({ chart }: any) => {

                if (this.suppressZoom) {
                  this.suppressZoom = false;
                  this.isDateFiltered = true;
                  return;
                }

                const min = chart.scales["x"].min;
                const max = chart.scales["x"].max;

                const from = new Date(min);
                from.setHours(from.getHours() - 1, 0, 0, 0); 

                const to = new Date(max);
                //to.setHours(23, 59, 59, 999);

                if ((to.getTime() - from.getTime()) > 1) {
                  this.tabletwo.filter(
                    { min: from, max: to },
                    'Timestamp',
                    'betweenDates'
                  );
                }

                this.suppressZoom = true;
                chart.resetZoom();

                // ✅ Add permanent annotation box
                chart.options.plugins.annotation.annotations = {
                  selectionBox: {
                    type: 'box',
                    xMin: min,
                    xMax: max,
                    backgroundColor: '#3DC3B020',
                    borderColor: '#3DC3B020',
                    borderWidth: 1
                  }
                };

                chart.update();
              }
            },
            pan: {
              enabled: false,
              mode: 'x'
            }
          }
        },
        onClick: (event, elements, chart) => {
          // Only reset if there’s an existing annotation
          if (chart.options.plugins.annotation?.annotations?.["selectionBox"]) {

            // Clear the annotation
            chart.options.plugins.annotation.annotations = {};

            // Optional: clear your custom from/to variables here
            // this.selectedRange = null;
            this.tabletwo.filter(null, 'Timestamp', 'betweenDates');
            this.isDateFiltered = false;

            
            chart.update();


          }
        }
      }
    }
  }

  createChartSubsystem(rowData: number[], backgroundColor = '#FBBF28'): ChartConfiguration<'line'> {
    return {

      type: 'line',
      data: {
        labels: Array(rowData.length).fill(''),
        datasets: [{
          ...this.baseDatasetStyle,
          backgroundColor: `${backgroundColor}26`,
          borderColor: backgroundColor,
          pointBackgroundColor: backgroundColor,
          pointBorderColor: backgroundColor,
          data: rowData
        }]
      },
      options: {
        devicePixelRatio: 4,
        interaction: {
          mode: null
        },
        responsive: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        }
      }
    };
  }

  selectSubsystem(subsystem: string) {
    this.selectedScatterFilters = this.activeFilterOptions.map(option => option.key);
    this.selectedSystemService.$selectedSubsystem.next(subsystem);
  }

  resetSubsystem() {
    this.selectedScatterFilters = this.activeFilterOptions.map(option => option.key);
    this.selectedSystemService.$selectedSubsystem.next(null);
  }

  onTableFiltered(event: any) {
    const chart = this.chartRef?.chart;
    if (!chart) return;

    const filteredRows = event.filteredValue ?? this.errors ?? [];

    chart.data.datasets.forEach((dataset: any) => {
      const severity = dataset.label?.includes('Warning') ? 'Warning' : 'Error';
      const isAnomalyDataset = dataset.label?.includes('Anomaly');
      const selectedIndices: number[] = dataset.data.map((point: any, index: number) => {
        const matchingRow = filteredRows.find(row => this.isMatchingScatterPoint(row, point, severity, isAnomalyDataset));

        return matchingRow ? index : -1;
      }).filter(index => index !== -1);

      dataset.backgroundColor = dataset.data.map((point: any, index: number) => {
        const matchingRow = filteredRows.find(row => this.isMatchingScatterPoint(row, point, severity, isAnomalyDataset));

        return selectedIndices.includes(index)
          ? this.getScatterPointColor(matchingRow)
          : this.dimmedPointColor;
      });
      dataset.borderColor = dataset.backgroundColor;
      dataset.pointRadius = dataset.data.map((_: any, index: number) => selectedIndices.includes(index) ? 6 : 3);
    });

    chart.update();
  }

  private isMatchingScatterPoint(
    row: { Severity?: string; BucketedTimestamp?: Date | string; sequence?: number; anomaly_details?: { anomaly_2sd_flag?: boolean | null } },
    point: { x: Date | string; y: number },
    severity: 'Error' | 'Warning',
    anomaly: boolean
  ): boolean {
    const rowSeverity = row?.Severity === 'Warning' ? 'Warning' : 'Error';
    const rowIsAnomaly = this.anomaliesEnabled && row?.anomaly_details?.anomaly_2sd_flag === true;

    return rowSeverity === severity
      && rowIsAnomaly === anomaly
      && new Date(row['BucketedTimestamp']).getTime() === new Date(point.x).getTime()
      && Number(row['sequence']) === Number(point.y);
  }


  parseDate = (s: string) => Date.parse(s.replace(" ", "T"));

  addSequenceNumbers(data) {
    const sorted = [...data].sort((a, b) => {
      const byBucket = this.parseDate(a.BucketedTimestamp) - this.parseDate(b.BucketedTimestamp);
      return byBucket !== 0 ? byBucket : (this.parseDate(a.Timestamp) - this.parseDate(b.Timestamp));
    });

    let lastBucket: string | null = null;
    let count = 0;

    return sorted.map((item) => {
      if (item.BucketedTimestamp !== lastBucket) {
        lastBucket = item.BucketedTimestamp;
        count = 1;
      } else {
        count += 1;
      }
      return { ...item, sequence: count };
    });
  }

  refresh() {
    this.selectedSystemService.$refreshSubsystems.next();
  }

  showTooltip(key: string, event) {
    this.tooltip.nativeElement.target = event?.target;
    this.tooltip.nativeElement.innerHTML = this.translocoService.translate(key);
    this.tooltip.nativeElement.visible = true;
  }

  refreshEvents() {
    this.selectedSystemService.$refreshEvents.next();
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;
    if(event?.data.length > 0) {
    event?.data?.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'errors':
          val1 = a["metrics"]["time_windows"]["" + this.selectedRange]["ErrorEvents"] ?? 0;
          val2 = b["metrics"]["time_windows"]["" + this.selectedRange]["ErrorEvents"] ?? 0;
          break;
        case 'warnings':
          val1 = a["metrics"]["time_windows"]["" + this.selectedRange]["Warnings"] ?? 0;
          val2 = b["metrics"]["time_windows"]["" + this.selectedRange]["Warnings"] ?? 0;
          break;
        case 'anomalies':
          val1 = this.getSubsystemAnomalyCount(a);
          val2 = this.getSubsystemAnomalyCount(b);
          break;

        case 'name': // string field
          val1 = a.subsystem?.toLowerCase() ?? '';
          val2 = b.subsystem?.toLowerCase() ?? '';
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }
  }


  customSortEvents(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data?.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'timestamp': // assuming this is a date field
          val1 = new Date(a.Timestamp);
          val2 = new Date(b.Timestamp);
          break;

        case 'code': // string field
          val1 = a.EventCode?.toLowerCase() ?? '';
          val2 = b.EventCode?.toLowerCase() ?? '';
          break;
        case 'severity': // string field
          val1 = a.Severity?.toLowerCase() ?? '';
          val2 = b.Severity?.toLowerCase() ?? '';
          break;
        case 'message': // string field
          val1 = a.MessageText?.toLowerCase() ?? '';
          val2 = b.MessageText?.toLowerCase() ?? '';
          break;
        case 'data': // string field
          val1 = a['Event Data']?.toLowerCase() ?? '';
          val2 = b['Event Data']?.toLowerCase() ?? '';
          break;
        case 'sw': // string field
          val1 = a['Software Version']?.toLowerCase() ?? '';
          val2 = b['Software Version']?.toLowerCase() ?? '';
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

  viewEventLogs() {
    this.modalService.$modalChanged.next({ modal: "eventlogs", open: true })
  }

  openHelp(section: string) {
    this.modalService.$helpModalChanged.next({ modal: section, open: true });
  }

}
